import React, { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { saveAs } from "file-saver";
import styled from "styled-components";

import SurveyVersions from "reports/Audit/VersionControl";

import {
  buildCsvContent,
  buildOtpExport,
  buildOtpExportFilename,
  resolveOtpQuestionStructure,
  selectOtpExportResponses,
} from "./utils";

const OTPExport = () => {
  const {
    rawData,
    coreData,
    cultureAuditReports,
    employees,
    surveyStructure,
    surveyDefinitions,
    surveyVersion,
  } = useSelector(
    (state) => ({
      rawData: state.audit?.raw_data,
      coreData: state.audit?.core_data,
      cultureAuditReports: state.debrief_schedule?.culture_audit_reports,
      employees: state.employees,
      surveyStructure: state.surveystructure?.survey_structure,
      surveyDefinitions: state.surveyquestions?.survey_questions,
      surveyVersion: state.audit?.survey_version,
    }),
    shallowEqual
  );

  const isAdmin = employees?.userEmp?.account_type === 5;
  const reports = cultureAuditReports?.response || [];

  const selectedSurvey = useMemo(() => {
    const sortedReports = [...reports].sort((a, b) => b.id - a.id);

    if (surveyVersion?.sort_order) {
      return sortedReports.find(
        (report) =>
          Number(report?.question_sort_order) ===
            Number(surveyVersion.sort_order) ||
          Number(report?.question?.sort_order) === Number(surveyVersion.sort_order)
      );
    }

    return sortedReports[0];
  }, [reports, surveyVersion]);

  const selectedSortOrder =
    surveyVersion?.sort_order ||
    selectedSurvey?.question_sort_order ||
    selectedSurvey?.question?.sort_order;

  const questionStructure = useMemo(
    () =>
      resolveOtpQuestionStructure({
        coreData,
        surveyDefinitions,
        selectedSortOrder,
        surveyVersion,
        selectedSurvey,
      }),
    [coreData, surveyDefinitions, selectedSortOrder, surveyVersion, selectedSurvey]
  );

  const responses = useMemo(
    () =>
      selectOtpExportResponses({
        rawData,
        cultureAuditReports: reports,
        selectedSortOrder,
      }),
    [rawData, reports, selectedSortOrder]
  );

  const emailByEmployeeId = useMemo(() => {
    const mapping = {};

    (employees?.employees || []).forEach((employee) => {
      if (employee?.id) {
        mapping[employee.id] = employee?.email || "";
      }
    });

    return mapping;
  }, [employees?.employees]);

  const categoryDefinitions = useMemo(
    () =>
      surveyStructure?.[0]?.categories ||
      coreData?.categories?.[0]?.categories ||
      [],
    [surveyStructure, coreData]
  );

  const exportData = useMemo(
    () =>
      buildOtpExport({
        responses,
        questionStructure,
        categoryDefinitions,
        emailByEmployeeId,
      }),
    [responses, questionStructure, categoryDefinitions, emailByEmployeeId]
  );

  const filename = buildOtpExportFilename(selectedSortOrder);

  const downloadCsv = (data = exportData, downloadFilename = filename) => {
    if (!data?.rows?.length) {
      return;
    }

    const csvContent = buildCsvContent(data.rows, data.headerKeys);
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    saveAs(blob, downloadFilename);
  };

  const buildSurveyExport = (survey, index) => {
    const surveySortOrder =
      survey?.question_sort_order || survey?.question?.sort_order || selectedSortOrder;
    const surveyQuestionStructure = resolveOtpQuestionStructure({
      coreData,
      surveyDefinitions,
      selectedSortOrder: surveySortOrder,
      surveyVersion: survey?.question,
      selectedSurvey: survey,
    });
    const data = buildOtpExport({
      responses: survey?.responses || [],
      questionStructure: surveyQuestionStructure,
      categoryDefinitions,
      emailByEmployeeId,
    });

    return {
      data,
      filename: buildOtpExportFilename(
        surveySortOrder,
        `response-${survey?.id || index}`
      ),
      surveySortOrder,
    };
  };

  if (!employees?.userEmp || !questionStructure) {
    return <Page>Loading OTP export data...</Page>;
  }

  if (!isAdmin) {
    return <Page>Access restricted.</Page>;
  }

  return (
    <Page>
      <HeaderRow>
        <div>
          <Title>OTP Export</Title>
          <Description>
            Export the selected OTP survey version with member tags, question
            scores, and comment responses.
          </Description>
        </div>
        <VersionControl>
          <SurveyVersions label />
        </VersionControl>
      </HeaderRow>

      <SummaryGrid>
        <SummaryItem>
          <SummaryLabel>Survey Version</SummaryLabel>
          <SummaryValue>{selectedSortOrder || "Unknown"}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Responses</SummaryLabel>
          <SummaryValue>{exportData.rows.length}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Questions</SummaryLabel>
          <SummaryValue>{exportData.questionDefinitions.length}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Comments</SummaryLabel>
          <SummaryValue>{exportData.commentDefinitions.length}</SummaryValue>
        </SummaryItem>
      </SummaryGrid>

      <Actions>
        <DownloadButton
          type="button"
          onClick={() => downloadCsv()}
          disabled={exportData.rows.length === 0}
        >
          Download Latest OTP CSV
        </DownloadButton>
        <Filename>{filename}</Filename>
      </Actions>

      {exportData.rows.length === 0 && (
        <EmptyState>No OTP responses are available for this survey version.</EmptyState>
      )}

      <SurveyTable>
        <thead>
          <tr>
            <TableHeader>Name</TableHeader>
            <TableHeader>Date</TableHeader>
            <TableHeader>Version</TableHeader>
            <TableHeader>Responses</TableHeader>
            <TableHeader>Action</TableHeader>
          </tr>
        </thead>
        <tbody>
          {reports.map((survey, index) => {
            const surveyExport = buildSurveyExport(survey, index);
            const dateString = survey?.date || survey?.created_at || "";
            const name =
              survey?.name ||
              survey?.report_name ||
              survey?.question?.title ||
              survey?.question?.questions?.title ||
              `Response ${survey?.id || index}`;
            const count = survey?.responses?.length || 0;

            return (
              <tr key={survey?.id || index}>
                <TableCell>{name}</TableCell>
                <TableCell>{dateString}</TableCell>
                <TableCell>{surveyExport.surveySortOrder || ""}</TableCell>
                <TableCell>{count}</TableCell>
                <TableCell>
                  {surveyExport.data.rows.length > 0 ? (
                    <SmallButton
                      type="button"
                      onClick={() =>
                        downloadCsv(surveyExport.data, surveyExport.filename)
                      }
                    >
                      Download CSV
                    </SmallButton>
                  ) : (
                    <Muted>No data</Muted>
                  )}
                </TableCell>
              </tr>
            );
          })}
        </tbody>
      </SurveyTable>
    </Page>
  );
};

const Page = styled.div`
  padding: 32px;
  color: #1f2933;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
`;

const Description = styled.p`
  max-width: 720px;
  margin: 0;
  color: #52606d;
  font-size: 14px;
`;

const VersionControl = styled.div`
  min-width: 240px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const SummaryItem = styled.div`
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  padding: 16px;
  background: #ffffff;
`;

const SummaryLabel = styled.div`
  color: #7b8794;
  font-size: 12px;
  margin-bottom: 8px;
`;

const SummaryValue = styled.div`
  font-size: 22px;
  font-weight: 700;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DownloadButton = styled.button`
  border: none;
  border-radius: 6px;
  background: ${(props) => (props.disabled ? "#cbd2d9" : "#1f6feb")};
  color: #ffffff;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  font-weight: 600;
  padding: 10px 16px;
`;

const Filename = styled.span`
  color: #7b8794;
  font-size: 13px;
`;

const SurveyTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 24px;
  background: #ffffff;
`;

const TableHeader = styled.th`
  border-bottom: 1px solid #d9e2ec;
  color: #52606d;
  font-size: 12px;
  font-weight: 700;
  padding: 10px 8px;
  text-align: left;
`;

const TableCell = styled.td`
  border-bottom: 1px solid #f0f4f8;
  font-size: 13px;
  padding: 10px 8px;
`;

const SmallButton = styled.button`
  border: none;
  border-radius: 6px;
  background: #1f6feb;
  color: #ffffff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 12px;
`;

const Muted = styled.span`
  color: #7b8794;
  font-size: 12px;
`;

const EmptyState = styled.div`
  margin-top: 24px;
  color: #7b8794;
`;

export default OTPExport;
